import { useEffect, useState } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';
import { Loader2, BarChart2, TrendingUp, ShoppingBag, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { vendorApi } from '@/services/api';
import { getVendorIdFromToken } from '@/services/auth';
import { formatNPR } from '@/utils/format';
import { Card, CardContent, CardHeader, CardTitle } from '@nepal-ecom/ui';

interface AnalyticsData {
  totalRevenue?: number;
  totalOrders?: number;
  totalViews?: number;
  conversionRate?: number;
  revenueByDay?: Array<{ date: string; revenue: number }>;
  ordersByDay?: Array<{ date: string; orders: number }>;
  topProducts?: Array<{ id: string; name: string; totalSold: number; revenue: number }>;
}

const num = (v: number | undefined | null) => v ?? 0;

export function Analytics() {
  const [data, setData] = useState<AnalyticsData>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const vendorId = getVendorIdFromToken();
    if (!vendorId) { setLoading(false); return; }
    vendorApi.getAnalytics(vendorId)
      .then((res) => setData(res.data?.data ?? {}))
      .catch((e) => toast.error('Could not load analytics', { description: (e as Error).message }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="py-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>;
  }

  const revenueByDay = data.revenueByDay ?? [];
  const ordersByDay = data.ordersByDay ?? [];
  const topProducts = data.topProducts ?? [];

  return (
    <div className="animate-fade-in space-y-5">
      <PageHeader title="Analytics" description="How your store is performing." />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 inline-flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3" /> Revenue
            </p>
            <p className="text-2xl font-bold text-emerald-700 tabular-nums">{formatNPR(num(data.totalRevenue))}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 inline-flex items-center gap-1.5">
              <ShoppingBag className="h-3 w-3" /> Orders
            </p>
            <p className="text-2xl font-bold text-slate-900 tabular-nums">{num(data.totalOrders)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 inline-flex items-center gap-1.5">
              <Eye className="h-3 w-3" /> Product views
            </p>
            <p className="text-2xl font-bold text-slate-900 tabular-nums">{num(data.totalViews)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 inline-flex items-center gap-1.5">
              <BarChart2 className="h-3 w-3" /> Conversion
            </p>
            <p className="text-2xl font-bold text-slate-900 tabular-nums">{num(data.conversionRate).toFixed(1)}%</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card>
          <CardHeader><CardTitle>Revenue trend</CardTitle></CardHeader>
          <CardContent>
            {revenueByDay.length === 0 ? (
              <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={revenueByDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(d) => format(new Date(d), 'dd MMM')} tick={{ fontSize: 11, fill: '#94A3B8' }} />
                  <YAxis tickFormatter={(v) => `रु.${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#94A3B8' }} />
                  <Tooltip formatter={(v: number) => formatNPR(v)} />
                  <Area type="monotone" dataKey="revenue" stroke="#D95100" fill="#D9510033" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Orders by day</CardTitle></CardHeader>
          <CardContent>
            {ordersByDay.length === 0 ? (
              <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={ordersByDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(d) => format(new Date(d), 'dd MMM')} tick={{ fontSize: 11, fill: '#94A3B8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} />
                  <Tooltip />
                  <Bar dataKey="orders" fill="#D95100" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {topProducts.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Top products</CardTitle></CardHeader>
          <CardContent>
            <ul className="divide-y divide-slate-100">
              {topProducts.slice(0, 10).map((p, i) => (
                <li key={p.id} className="flex items-center gap-3 py-3">
                  <span className="text-sm font-bold tabular-nums text-slate-400 w-6">#{i + 1}</span>
                  <p className="flex-1 text-sm font-semibold text-slate-900 truncate">{p.name}</p>
                  <p className="text-xs text-slate-500 tabular-nums">{p.totalSold} sold</p>
                  <p className="text-sm font-bold text-slate-900 tabular-nums">{formatNPR(p.revenue)}</p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
